import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Input, Row, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { isValidPhone } from "../../../utils/phone";

const { Text, Paragraph } = Typography;

export interface ContactSectionValue {
    email: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    postcode: string;
    country: string;
    website: string;
    supportEmail: string;
}

export const DEFAULT_CONTACT: ContactSectionValue = {
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    country: "",
    website: "",
    supportEmail: "",
};

// Mirror the backend validators (ClientService.IsValidEmail / IsValidPhone /
// IsValidWebsiteUrl) so the form rejects exactly what the API would 400 on.
// Phone validation is shared with the member enrolment forms via utils/phone.
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isValidEmail(email: string): boolean {
    return EMAIL_REGEX.test(email);
}

export function isValidWebsiteUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

// Every field is optional — format checks only apply to non-empty input.
export function isValid(value: ContactSectionValue): boolean {
    const email = value.email.trim();
    const supportEmail = value.supportEmail.trim();
    const website = value.website.trim();
    const phone = value.phone.trim();
    return (
        (email.length === 0 || isValidEmail(email)) &&
        (supportEmail.length === 0 || isValidEmail(supportEmail)) &&
        (website.length === 0 || isValidWebsiteUrl(website)) &&
        (phone.length === 0 || isValidPhone(phone))
    );
}

interface FieldProps {
    label: string;
    testId: string;
    value: string;
    onChange: (next: string) => void;
    error?: string | null;
    placeholder?: string;
    /** HTML input type — e.g. `tel` for the phone field so mobile shows the phone keypad. */
    type?: "text" | "tel" | "email" | "url";
}

const ContactField: React.FC<FieldProps> = ({ label, testId, value, onChange, error, placeholder, type }) => (
    <Form.Item
        label={label}
        validateStatus={error ? "error" : undefined}
        help={error ?? undefined}
        style={{ marginBottom: 12 }}
    >
        <Input
            data-testid={testId}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
        />
    </Form.Item>
);

// Controlled contact-details section: the club's public-facing email, phone,
// address, website and support email. All fields optional free text.
export const ContactSection: React.FC<SectionPanelProps<ContactSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => {
    const email = value.email.trim();
    const supportEmail = value.supportEmail.trim();
    const website = value.website.trim();
    const phone = value.phone.trim();

    const emailError = email.length > 0 && !isValidEmail(email) ? "Enter a valid email address." : null;
    const supportEmailError =
        supportEmail.length > 0 && !isValidEmail(supportEmail) ? "Enter a valid email address." : null;
    const websiteError =
        website.length > 0 && !isValidWebsiteUrl(website)
            ? "Enter an absolute URL starting with http:// or https://."
            : null;
    const phoneError = phone.length > 0 && !isValidPhone(phone) ? "Enter a valid phone number." : null;

    const set = (field: keyof ContactSectionValue) => (next: string) =>
        onChange({ ...value, [field]: next });

    return (
        <Form layout="vertical" data-testid="settings-contact-form" disabled={readOnly}>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <ContactField
                        label="Email"
                        testId="settings-contact-email"
                        value={value.email}
                        onChange={set("email")}
                        error={emailError}
                        placeholder="enquiries@yourclub.com"
                    />
                </Col>
                <Col xs={24} md={12}>
                    <ContactField
                        label="Support email"
                        testId="settings-contact-support-email"
                        value={value.supportEmail}
                        onChange={set("supportEmail")}
                        error={supportEmailError}
                        placeholder="support@yourclub.com"
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <ContactField
                        label="Phone"
                        testId="settings-contact-phone"
                        type="tel"
                        value={value.phone}
                        onChange={set("phone")}
                        error={phoneError}
                        placeholder="+44 20 7946 0123"
                    />
                </Col>
                <Col xs={24} md={12}>
                    <ContactField
                        label="Website"
                        testId="settings-contact-website"
                        value={value.website}
                        onChange={set("website")}
                        error={websiteError}
                        placeholder="https://yourclub.com"
                    />
                </Col>
            </Row>
            <ContactField
                label="Address line 1"
                testId="settings-contact-address1"
                value={value.addressLine1}
                onChange={set("addressLine1")}
            />
            <ContactField
                label="Address line 2"
                testId="settings-contact-address2"
                value={value.addressLine2}
                onChange={set("addressLine2")}
            />
            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <ContactField
                        label="City"
                        testId="settings-contact-city"
                        value={value.city}
                        onChange={set("city")}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <ContactField
                        label="Postcode"
                        testId="settings-contact-postcode"
                        value={value.postcode}
                        onChange={set("postcode")}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <ContactField
                        label="Country"
                        testId="settings-contact-country"
                        value={value.country}
                        onChange={set("country")}
                    />
                </Col>
            </Row>
        </Form>
    );
};

// Adapter that mounts ContactSection inside the Settings shell: loads current
// contact config from /config, tracks dirty state, and saves via PUT.
export const ContactSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<ContactSectionValue | null>(null);
    const [value, setValue] = useState<ContactSectionValue>(DEFAULT_CONTACT);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                // Clients created before FORT-127 only have email set — the
                // other fields fall back to empty rather than erroring.
                const contact = config?.settings.contact;
                const loaded: ContactSectionValue = {
                    email: contact?.email ?? "",
                    phone: contact?.phone ?? "",
                    addressLine1: contact?.addressLine1 ?? "",
                    addressLine2: contact?.addressLine2 ?? "",
                    city: contact?.city ?? "",
                    postcode: contact?.postcode ?? "",
                    country: contact?.country ?? "",
                    website: contact?.website ?? "",
                    supportEmail: contact?.supportEmail ?? "",
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !isValid(value)) return;
        setSaving(true);
        setSaveError(null);
        // Persist and re-baseline on the trimmed values so the form reflects
        // exactly what the server stored.
        const trimmed: ContactSectionValue = {
            email: value.email.trim(),
            phone: value.phone.trim(),
            addressLine1: value.addressLine1.trim(),
            addressLine2: value.addressLine2.trim(),
            city: value.city.trim(),
            postcode: value.postcode.trim(),
            country: value.country.trim(),
            website: value.website.trim(),
            supportEmail: value.supportEmail.trim(),
        };
        try {
            await service.updateContact(clientId, trimmed);
            setInitial(trimmed);
            setValue(trimmed);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-contact-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                The club&apos;s public contact details. All fields are optional — leave anything
                you don&apos;t want to publish empty.
            </Paragraph>
            <ContactSection value={value} onChange={setValue} />
            {saveError && <Alert type="error" message={saveError} data-testid="settings-contact-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-contact-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !isValid(value)}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-contact-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
